/**
 * Typed client for the ETERNALLY API. All requests are same-origin;
 * the session cookie does the authenticating, signed URLs do the media.
 */

export interface ShareMeta {
  label: string;
  authenticated: boolean;
  displayName?: string;
}

export interface ViewerTrack {
  id: string;
  label: string;
  lufs: number;
  truePeakDb: number;
  url: string;
}

export interface ViewerAngle {
  id: string;
  label: string;
  playlistUrl: string;
  /** Signed still of the angle's first frame, shown before decode. */
  posterUrl?: string;
}

export interface ViewerTimecode {
  start: string;
  frameRate: number;
  dropFrame: boolean;
}

export interface ViewerManifest {
  assetId: string;
  title: string;
  /**
   * Ingest timestamp. A re-ingest keeps every media path and changes
   * every byte, so this is what the local media cache versions its
   * copy against. Optional because a player build can reach an API
   * that predates the field; without it the local cache stays off.
   */
  createdAt?: string;
  durationSeconds: number;
  targetLufs: number;
  video: { playlistUrl: string; posterUrl?: string; angles?: ViewerAngle[] };
  timecode?: ViewerTimecode;
  /** Upload state derived server-side from arrived payload bytes. */
  ingest?: { ready: boolean; progress: number };
  /** Operator-exposed facts, already filtered server-side. */
  meta?: { label: string; value: string }[];
  tracks: ViewerTrack[];
}

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const merged: RequestInit = { credentials: 'same-origin', ...init };
  if (init?.body) {
    merged.headers = { 'content-type': 'application/json', ...init.headers };
  }
  const response = await fetch(path, merged);
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) {
        message = body.error;
      }
    } catch {
      // Non-JSON error body; the status line is all there is.
    }
    throw new ApiError(response.status, message);
  }
  return response.json() as Promise<T>;
}

export interface AssetSummary {
  assetId: string;
  title: string;
  durationSeconds: number;
  trackCount: number;
  createdAt: string;
  ready?: boolean;
  progress?: number;
}

export interface AdminTrack {
  id: string;
  label: string;
  channels: number[];
  lufs: number;
  truePeakDb: number;
  url: string;
}

export interface AdminMetaField {
  id: string;
  label: string;
  value: string;
  exposed: boolean;
}

export interface AdminManifest {
  assetId: string;
  title: string;
  durationSeconds: number;
  targetLufs: number;
  tracks: AdminTrack[];
  meta?: AdminMetaField[];
  video?: { playlistUrl: string; angles?: { id: string; label: string; playlistUrl: string }[] };
  createdAt: string;
}

export interface PresentationUpdate {
  title?: string;
  tracks?: { id: string; label: string }[];
  meta?: AdminMetaField[];
}

export interface AdminShare {
  token: string;
  assetId: string;
  label: string;
  expiresAt: string;
  createdAt: string;
  revoked: boolean;
  active: boolean;
  url: string;
}

export interface LogEntry {
  userId: string;
  displayName: string;
  event: string;
  positionSeconds: number;
  at: string;
}

export interface SessionInfo {
  authenticated: boolean;
  scope?: string;
  displayName?: string;
  role?: 'viewer' | 'admin';
}

export const adminApi = {
  session: () => request<SessionInfo>('/api/session'),

  registerOptions: (bootstrapToken: string) =>
    request<{ challengeId: string; options: unknown }>('/api/admin/register/options', {
      method: 'POST',
      body: JSON.stringify({ bootstrapToken }),
    }),

  registerVerify: (challengeId: string, response: unknown) =>
    request<{ ok: boolean }>('/api/admin/register/verify', {
      method: 'POST',
      body: JSON.stringify({ challengeId, response }),
    }),

  loginOptions: () =>
    request<{ challengeId: string; options: unknown }>('/api/admin/login/options', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  loginVerify: (challengeId: string, response: unknown) =>
    request<{ ok: boolean }>('/api/admin/login/verify', {
      method: 'POST',
      body: JSON.stringify({ challengeId, response }),
    }),

  assets: () => request<{ assets: AssetSummary[] }>('/api/admin/assets'),

  asset: (assetId: string) => request<AdminManifest>(`/api/admin/assets/${assetId}`),

  savePresentation: (assetId: string, update: PresentationUpdate) =>
    request<AdminManifest>(`/api/admin/assets/${assetId}/presentation`, {
      method: 'PUT',
      body: JSON.stringify(update),
    }),

  createShare: (assetId: string, label: string, expiresAt: string) =>
    request<{ share: AdminShare; url: string }>('/api/admin/shares', {
      method: 'POST',
      body: JSON.stringify({ assetId, label, expiresAt }),
    }),

  shares: () => request<{ shares: AdminShare[] }>('/api/admin/shares'),

  revokeShare: (token: string) =>
    request<{ ok: boolean }>(`/api/admin/shares/${token}`, { method: 'DELETE' }),

  extendShare: (token: string, expiresAt: string) =>
    request<AdminShare>(`/api/admin/shares/${token}/expiry`, {
      method: 'PUT',
      body: JSON.stringify({ expiresAt }),
    }),

  shareLog: (token: string) =>
    request<{ entries: LogEntry[]; progress: LogEntry[] }>(`/api/admin/shares/${token}/log`),
};

export const api = {
  shareMeta: (token: string) => request<ShareMeta>(`/api/share/${token}`),

  registerOptions: (token: string, displayName: string) =>
    request<{ challengeId: string; options: unknown }>(`/api/share/${token}/register/options`, {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    }),

  registerVerify: (token: string, challengeId: string, response: unknown) =>
    request<{ ok: boolean }>(`/api/share/${token}/register/verify`, {
      method: 'POST',
      body: JSON.stringify({ challengeId, response }),
    }),

  loginOptions: (token: string) =>
    request<{ challengeId: string; options: unknown }>(`/api/share/${token}/login/options`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  loginVerify: (token: string, challengeId: string, response: unknown) =>
    request<{ ok: boolean }>(`/api/share/${token}/login/verify`, {
      method: 'POST',
      body: JSON.stringify({ challengeId, response }),
    }),

  magicRequest: (token: string, email: string, displayName: string) =>
    request<{ ok: boolean; devLink?: string }>(`/api/share/${token}/magic/request`, {
      method: 'POST',
      body: JSON.stringify({ email, displayName }),
    }),

  manifest: (token: string) => request<ViewerManifest>(`/api/share/${token}/manifest`),

  progress: (token: string, positionSeconds: number, complete = false) =>
    request<{ ok: boolean }>(`/api/share/${token}/progress`, {
      method: 'POST',
      body: JSON.stringify({ positionSeconds, complete }),
    }),
};
